import { useEffect, useMemo, useState } from 'react';
import { Copy, Loader2, Lock, Pencil, Plus, RotateCcw, Shield, Trash2, Users } from 'lucide-react';
import {
  useApplyTemplateToUser,
  useCreateRoleTemplate,
  useDeleteRoleTemplate,
  useDeletedSystemTemplates,
  useRestoreSystemTemplate,
  useRoleTemplates,
  useUpdateRoleTemplate,
} from '../../hooks/useRoles';
import { usePeople } from '../../hooks/usePeople';
import { ApiError } from '../../lib/api';
import {
  ADMIN_TEMPLATE_KEY,
  emptyMatrix,
  type RoleTemplate,
  type UserPermissionMatrix,
} from '../../lib/permissionsApi';
import { RbacMatrixEditor } from '../rbac/RbacMatrixEditor';
import { Select } from '../ui/Select';

type View = 'list' | 'editor';

/**
 * What the editor opens on: an existing template to edit, or a new one —
 * empty, or a copy of another template ("Duplică").
 */
type EditorTarget = { mode: 'edit'; template: RoleTemplate } | { mode: 'new'; from?: RoleTemplate };

function cloneMatrix(matrix: UserPermissionMatrix): UserPermissionMatrix {
  return JSON.parse(JSON.stringify(matrix)) as UserPermissionMatrix;
}

function usersLabel(n: number): string {
  return n === 1 ? '1 utilizator' : `${n} utilizatori`;
}

/** The server's refusal, in words the admin can act on. */
function deleteErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'SELF_LOCKOUT') {
      return 'Ți-ai pierde accesul la Roluri și permisiuni: alege un șablon care îl include.';
    }
    if (err.code === 'ADMIN_TEMPLATE_PROTECTED') return 'Șablonul Admin nu poate fi șters.';
    if (err.code === 'TEMPLATE_IN_USE') {
      return 'Între timp, cineva a primit acest șablon. Alege unde să fie mutați.';
    }
  }
  return err instanceof Error ? err.message : 'Ștergerea șablonului a eșuat';
}

/**
 * Settings > Roles & Permissions (CLE-216, parent section 3.5). Lists system +
 * custom role templates, lets admins create (from scratch or from a copy),
 * edit and delete them with the shared RBAC matrix editor — every template
 * but Admin can be deleted, its users moved to another one — restores deleted
 * system templates, and bulk-assigns templates to team members with login
 * accounts.
 */
export function RolesPermissionsSettings() {
  const { data: templates, isLoading } = useRoleTemplates();
  const { data: deletedSystem } = useDeletedSystemTemplates();
  const restore = useRestoreSystemTemplate();
  const [view, setView] = useState<View>('list');
  const [target, setTarget] = useState<EditorTarget>({ mode: 'new' });
  const [deleting, setDeleting] = useState<RoleTemplate | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  const systemTemplates = useMemo(() => (templates ?? []).filter((t) => t.isSystem), [templates]);
  const customTemplates = useMemo(() => (templates ?? []).filter((t) => !t.isSystem), [templates]);

  function openEditor(next: EditorTarget) {
    setTarget(next);
    setView('editor');
  }

  if (view === 'editor') {
    return (
      <RoleTemplateEditor
        target={target}
        templates={templates ?? []}
        onDone={() => {
          setTarget({ mode: 'new' });
          setView('list');
        }}
      />
    );
  }

  const cardProps = (t: RoleTemplate) => ({
    template: t,
    onEdit: () => openEditor({ mode: 'edit', template: t }),
    onDuplicate: () => openEditor({ mode: 'new', from: t }),
    onDelete: () => setDeleting(t),
  });

  return (
    <div className="max-w-4xl">
      {/* Phones: the explanation on top, the action full width under it
          (squeezed beside the text it wrapped the text to a narrow column). */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between [&>button]:justify-center">
        <p className="text-sm text-gray-500">
          Șabloanele de rol definesc o matrice de permisiuni reutilizabilă. Toate se pot edita și
          șterge, în afară de Admin; creați șabloane noi de la zero sau pornind de la unul existent.
        </p>
        <button
          type="button"
          onClick={() => openEditor({ mode: 'new' })}
          className="ui-btn-primary inline-flex items-center gap-1.5 whitespace-nowrap"
        >
          <Plus size={15} />
          Șablon nou
        </button>
      </div>

      {isLoading && <p className="mt-6 text-sm text-gray-500">Se încarcă șabloanele...</p>}

      {/* System templates */}
      <h3 className="mt-6 flex items-center gap-1.5 text-sm font-semibold text-gray-700">
        <Lock size={14} /> Șabloane de sistem
      </h3>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        {systemTemplates.map((t) => (
          <TemplateCard key={t.id} {...cardProps(t)} />
        ))}
      </div>

      {/* Custom templates */}
      <h3 className="mt-6 flex items-center gap-1.5 text-sm font-semibold text-gray-700">
        <Shield size={14} /> Șabloane personalizate
      </h3>
      {customTemplates.length === 0 ? (
        <p className="mt-2 text-sm text-gray-400">Niciun șablon personalizat încă.</p>
      ) : (
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {customTemplates.map((t) => (
            <TemplateCard key={t.id} {...cardProps(t)} />
          ))}
        </div>
      )}

      {deletedSystem && deletedSystem.length > 0 && (
        <div className="mt-4 rounded-lg border border-dashed border-border px-3 py-2.5 text-sm text-gray-500">
          <span>Șabloane de sistem șterse: </span>
          {deletedSystem.map((d, i) => (
            <span key={d.key}>
              {i > 0 && ', '}
              <span className="text-gray-700">{d.name}</span>{' '}
              <button
                type="button"
                disabled={restore.isPending}
                onClick={() => {
                  setRestoreError(null);
                  restore.mutate(d.key, {
                    onError: (err) =>
                      setRestoreError(err instanceof Error ? err.message : 'Restaurarea a eșuat'),
                  });
                }}
                className="inline-flex items-center gap-1 text-brand-600 hover:underline disabled:opacity-50"
              >
                <RotateCcw size={12} />
                Restaurează
              </button>
            </span>
          ))}
          {restoreError && <p className="mt-1 text-red-600">{restoreError}</p>}
        </div>
      )}

      <UserAssignmentPanel templates={templates ?? []} />

      {deleting && (
        <DeleteTemplateDialog
          template={deleting}
          templates={templates ?? []}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

function TemplateCard({
  template,
  onEdit,
  onDuplicate,
  onDelete,
}: {
  template: RoleTemplate;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const isAdmin = template.systemKey === ADMIN_TEMPLATE_KEY;
  const userCount = template.userCount ?? 0;
  return (
    <div className="flex items-start justify-between gap-2 rounded-lg border border-border bg-card p-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900">{template.name}</p>
        {template.description && (
          <p className="mt-0.5 text-xs text-gray-500">{template.description}</p>
        )}
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          {template.isSystem && (
            <span className="inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-gray-500">
              Sistem
            </span>
          )}
          {userCount > 0 && (
            <span className="text-[11px] text-gray-400">{usersLabel(userCount)}</span>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={onEdit}
          className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          aria-label="Editează șablon"
          title="Editează"
        >
          <Pencil size={14} />
        </button>
        <button
          type="button"
          onClick={onDuplicate}
          className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          aria-label="Duplică șablon"
          title="Duplică"
        >
          <Copy size={14} />
        </button>
        {/* Admin is never deleted; every other template can be. */}
        {isAdmin ? (
          <span className="p-1.5 text-gray-300" title="Protejat: șablonul Admin nu se șterge">
            <Lock size={14} />
          </span>
        ) : (
          <button
            type="button"
            onClick={onDelete}
            className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
            aria-label="Șterge șablon"
            title="Șterge"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Delete a template. Unused: a plain confirmation. In use: its users have to
 * go somewhere, so the admin picks the template they move to (and get the
 * permissions of) before it is deleted.
 */
function DeleteTemplateDialog({
  template,
  templates,
  onClose,
}: {
  template: RoleTemplate;
  templates: RoleTemplate[];
  onClose: () => void;
}) {
  const del = useDeleteRoleTemplate();
  const [replaceWith, setReplaceWith] = useState('');
  const [error, setError] = useState<string | null>(null);
  const userCount = template.userCount ?? 0;
  const inUse = userCount > 0;
  const others = templates.filter((t) => t.id !== template.id);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && !del.isPending) onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, del.isPending]);

  function submit() {
    setError(null);
    if (inUse && !replaceWith) {
      setError('Alege șablonul pe care sunt mutați utilizatorii.');
      return;
    }
    del.mutate(
      { id: template.id, replaceWith: inUse ? replaceWith : undefined },
      { onSuccess: onClose, onError: (err) => setError(deleteErrorMessage(err)) },
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !del.isPending) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <h2 className="text-lg font-semibold text-gray-900">Șterge „{template.name}”</h2>
        {inUse ? (
          <>
            <p className="mt-2 text-sm text-gray-600">
              {usersLabel(userCount)} {userCount === 1 ? 'are' : 'au'} acest șablon. Mută-i pe alt
              șablon — primesc permisiunile lui — apoi șablonul se șterge.
            </p>
            <Select
              label="Mută-i pe"
              value={replaceWith}
              onChange={setReplaceWith}
              placeholder="Alege un șablon..."
              options={others.map((t) => ({ value: t.id, label: t.name }))}
              className="mt-3"
            />
          </>
        ) : (
          <p className="mt-2 text-sm text-gray-600">
            Nimeni nu are acest șablon.
            {template.isSystem
              ? ' Îl poți restaura oricând, cu permisiunile implicite.'
              : ' Ștergerea nu poate fi anulată.'}
          </p>
        )}
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={del.isPending}
            className="ui-btn-secondary"
          >
            Anulează
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={del.isPending}
            className="ui-btn-danger inline-flex items-center gap-1.5"
          >
            {del.isPending && <Loader2 size={14} className="animate-spin" />}
            {inUse ? 'Mută și șterge' : 'Șterge'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RoleTemplateEditor({
  target,
  templates,
  onDone,
}: {
  target: EditorTarget;
  templates: RoleTemplate[];
  onDone: () => void;
}) {
  const create = useCreateRoleTemplate();
  const update = useUpdateRoleTemplate();
  const template = target.mode === 'edit' ? target.template : null;
  const from = target.mode === 'new' ? target.from : undefined;
  const [name, setName] = useState(template?.name ?? (from ? `Copie – ${from.name}` : ''));
  const [description, setDescription] = useState(template?.description ?? from?.description ?? '');
  const [matrix, setMatrix] = useState<UserPermissionMatrix>(() => {
    const source = template ?? from;
    return source ? cloneMatrix(source.permissions) : emptyMatrix();
  });
  const [startFrom, setStartFrom] = useState(from?.id ?? '');
  const [error, setError] = useState<string | null>(null);

  const isEdit = Boolean(template);
  const pending = create.isPending || update.isPending;

  function pickStart(id: string) {
    setStartFrom(id);
    const source = templates.find((t) => t.id === id);
    setMatrix(source ? cloneMatrix(source.permissions) : emptyMatrix());
  }

  function handleSave() {
    setError(null);
    if (!name.trim()) {
      setError('Numele este obligatoriu');
      return;
    }
    const input = {
      name: name.trim(),
      description: description.trim() || null,
      permissions: matrix,
    };
    const onError = (err: unknown) =>
      setError(err instanceof Error ? err.message : 'Salvarea șablonului a eșuat');
    if (isEdit && template) {
      update.mutate({ id: template.id, input }, { onSuccess: onDone, onError });
    } else {
      create.mutate(input, { onSuccess: onDone, onError });
    }
  }

  return (
    <div className="max-w-4xl">
      <button type="button" onClick={onDone} className="text-sm text-brand-600 hover:underline">
        ← Înapoi la șabloane
      </button>
      <h3 className="mt-3 text-lg font-semibold text-gray-900">
        {isEdit ? `Editează "${template?.name}"` : 'Șablon nou'}
      </h3>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="block text-sm font-medium text-gray-700">
          Nume <span className="text-red-500">*</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ex. Dezvoltator Junior"
            className="ui-input mt-1"
          />
        </label>
        <label className="block text-sm font-medium text-gray-700">
          Descriere
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Descriere scurtă"
            className="ui-input mt-1"
          />
        </label>
      </div>

      <h4 className="mt-5 text-sm font-semibold text-gray-700">Permisiuni</h4>
      {!isEdit && (
        <div className="mt-2 max-w-sm">
          <Select
            label="Pornește de la"
            value={startFrom}
            onChange={pickStart}
            options={[
              { value: '', label: 'Gol (nicio permisiune)' },
              ...templates.map((t) => ({ value: t.id, label: t.name })),
            ]}
          />
          <p className="mt-1 text-xs text-gray-500">
            Copiază permisiunile unui șablon existent; le poți modifica mai jos.
          </p>
        </div>
      )}
      {isEdit && (
        <p className="mt-1 text-xs text-gray-500">
          Modificările salvate se aplică automat tuturor conturilor care au acest șablon — nu mai
          trebuie să resetați fiecare utilizator manual.
        </p>
      )}
      <div className="mt-2">
        <RbacMatrixEditor value={matrix} onChange={setMatrix} showTemplatePicker={false} />
      </div>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      <div className="mt-5 flex justify-end gap-2">
        <button type="button" onClick={onDone} className="ui-btn-secondary">
          Anulează
        </button>
        <button type="button" onClick={handleSave} disabled={pending} className="ui-btn-primary">
          {pending ? 'Se salvează...' : isEdit ? 'Salvează modificările' : 'Creează șablon'}
        </button>
      </div>
    </div>
  );
}

function UserAssignmentPanel({ templates }: { templates: RoleTemplate[] }) {
  const { data: people } = usePeople({ active: 'true' });
  const apply = useApplyTemplateToUser();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkTemplateId, setBulkTemplateId] = useState('');
  const [status, setStatus] = useState<string | null>(null);

  // Only members with a login account can have a role template applied.
  const accountHolders = (people ?? []).filter((p) => p.userId);

  function toggle(userId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  async function applyBulk() {
    if (!bulkTemplateId || selected.size === 0) return;
    setStatus(null);
    const ids = [...selected];
    await Promise.all(
      ids.map((userId) =>
        apply.mutateAsync({ userId, roleTemplateId: bulkTemplateId }).catch(() => null),
      ),
    );
    setStatus(
      `Șablon aplicat la ${ids.length} ${ids.length === 1 ? 'utilizator' : 'utilizatori'}.`,
    );
    setSelected(new Set());
  }

  return (
    <div className="mt-8">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-700">
        <Users size={14} /> Atribuie șabloane utilizatorilor
      </h3>
      <p className="mt-1 text-sm text-gray-500">
        Selectați membrii cu conturi de autentificare și aplicați un șablon de rol în masă. Aceasta
        înlocuiește matricea lor curentă de permisiuni.
      </p>

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <Select
          label="Șablon"
          clearable
          value={bulkTemplateId}
          onChange={setBulkTemplateId}
          placeholder="Selectați un șablon..."
          options={templates.map((t) => ({ value: t.id, label: t.name }))}
          className="w-56"
        />
        <button
          type="button"
          onClick={applyBulk}
          disabled={!bulkTemplateId || selected.size === 0 || apply.isPending}
          className="ui-btn-primary"
        >
          {apply.isPending ? 'Se aplică...' : `Aplică la ${selected.size} selectați`}
        </button>
      </div>

      {status && (
        <p className="mt-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-2 text-sm text-emerald-600">
          {status}
        </p>
      )}

      <div className="mt-3 divide-y divide-border rounded-lg border border-border">
        {accountHolders.length === 0 && (
          <p className="px-4 py-3 text-sm text-gray-400">
            Niciun membru al echipei cu cont de autentificare încă.
          </p>
        )}
        {accountHolders.map((p) => (
          <label
            key={p.id}
            className="flex cursor-pointer items-center gap-3 px-4 py-2.5 hover:bg-accent"
          >
            <input
              type="checkbox"
              checked={selected.has(p.userId as string)}
              onChange={() => toggle(p.userId as string)}
              className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            <span className="text-sm font-medium text-gray-900">{p.fullName}</span>
            <span className="text-xs text-gray-400">{p.email}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
